import React, { useState } from 'react';
import { MailX, MailCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function UnsubscribePage() {
  const params = new URLSearchParams(window.location.search);
  const email = params.get('email') || '';
  const token = params.get('t') || '';
  const type = params.get('type') || 'updates'; // 'updates' | 'events' | 'digest'
  const isEvents = type === 'events';
  const isDigest = type === 'digest';
  const [status, setStatus] = useState('idle'); // idle | working | unsubscribed | resubscribed | error
  const [errorMsg, setErrorMsg] = useState('');

  const unsubPath = isDigest ? 'unsubscribe/weekly-digest' : isEvents ? 'unsubscribe/event-alerts' : 'unsubscribe';
  const resubPath = isDigest ? 'resubscribe/weekly-digest' : isEvents ? 'resubscribe/event-alerts' : 'resubscribe';
  const kind = isDigest ? 'weekly Track ID digest' : isEvents ? 'event alert' : 'update';

  const call = async (path, nextStatus) => {
    setStatus('working');
    try {
      const r = await fetch(`${BACKEND_URL}/api/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token })
      });
      const d = await r.json();
      if (r.ok) setStatus(nextStatus);
      else { setStatus('error'); setErrorMsg(d.detail || 'Something went wrong'); }
    } catch (e) {
      setStatus('error');
      setErrorMsg('Network error — please try again');
    }
  };

  const valid = email && token;

  return (
    <div style={{
      minHeight: '100vh', background: 'linear-gradient(170deg, #0a0e1a 0%, #12122b 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
    }}>
      <div data-testid="unsubscribe-card" style={{
        width: '100%', maxWidth: '440px', background: '#0d1224',
        border: '1px solid rgba(0, 212, 255, 0.25)', borderRadius: '16px',
        padding: '2.25rem 1.75rem', textAlign: 'center'
      }}>
        <div style={{ fontSize: '28px', fontWeight: 900, letterSpacing: '0.12em', color: '#ff0000', marginBottom: '4px' }}>
          BEATSEEK
        </div>
        <div style={{ fontSize: '10px', letterSpacing: '0.28em', color: '#00d4ff', textTransform: 'uppercase', fontWeight: 700, marginBottom: '1.75rem' }}>
          Email Preferences
        </div>

        {!valid ? (
          <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
            This unsubscribe link is missing information. Please use the link from the bottom of a BEATSEEK email.
          </p>
        ) : status === 'unsubscribed' ? (
          <>
            <MailX size={40} color="#4ade80" style={{ margin: '0 auto 12px' }} />
            <p data-testid="unsubscribe-success" style={{ color: '#fff', fontWeight: 600, marginBottom: '6px' }}>You're unsubscribed</p>
            <p style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
              <span style={{ color: '#00d4ff' }}>{email}</span> will no longer receive BEATSEEK {isDigest ? 'weekly Track ID digest emails' : isEvents ? 'new-event emails' : 'update emails'}.
            </p>
            <button
              data-testid="resubscribe-btn"
              onClick={() => call(resubPath, 'resubscribed')}
              style={{
                background: 'none', border: '1px solid rgba(0,212,255,0.4)', color: '#00d4ff',
                padding: '9px 22px', borderRadius: '999px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer'
              }}
            >
              Changed your mind? Resubscribe
            </button>
          </>
        ) : status === 'resubscribed' ? (
          <>
            <MailCheck size={40} color="#00d4ff" style={{ margin: '0 auto 12px' }} />
            <p data-testid="resubscribe-success" style={{ color: '#fff', fontWeight: 600, marginBottom: '6px' }}>Welcome back!</p>
            <p style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
              <span style={{ color: '#00d4ff' }}>{email}</span> is back on the {kind} list.
            </p>
          </>
        ) : (
          <>
            <p style={{ color: '#e6e9f2', fontSize: '0.95rem', marginBottom: '6px' }}>
              {isDigest ? 'Unsubscribe from the weekly Track ID digest?' : isEvents ? 'Turn off BEATSEEK new-event alert emails?' : 'Unsubscribe from BEATSEEK update emails?'}
            </p>
            <p style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
              <span style={{ color: '#00d4ff' }}>{email}</span>
            </p>
            {status === 'error' && (
              <p data-testid="unsubscribe-error" style={{ color: '#f87171', fontSize: '0.82rem', marginBottom: '1rem' }}>{errorMsg}</p>
            )}
            <button
              data-testid="unsubscribe-confirm-btn"
              onClick={() => call(unsubPath, 'unsubscribed')}
              disabled={status === 'working'}
              style={{
                background: 'linear-gradient(135deg, #ff1a1a, #c00)', border: 'none', color: '#fff',
                padding: '11px 30px', borderRadius: '999px', fontSize: '0.88rem', fontWeight: 700,
                cursor: 'pointer', opacity: status === 'working' ? 0.6 : 1
              }}
            >
              {status === 'working' ? 'Working...' : (isEvents ? 'Turn off event alerts' : 'Unsubscribe me')}
            </button>
          </>
        )}

        <p style={{ color: '#565b70', fontSize: '0.72rem', marginTop: '1.75rem' }}>
          You'll still receive essential account emails. · <a href="https://thebeatseek.com" style={{ color: '#565b70' }}>thebeatseek.com</a>
        </p>
      </div>
    </div>
  );
}
